/* The server's time is heard five times a second, each hearing delayed by a different amount.
   Delay only ever makes the server seem earlier than it is, so the hearing that puts it latest
   in the last `windowMs` is nearest the truth. The clock moves to that at no more than `slew` of
   its own pace, and steps only for an error past `stepMs`: every animation in the shared city
   runs on this clock, and a clock set afresh by each hearing hitched them all as often. */
const CLOCK = { windowMs: 10_000, slew: 0.05, stepMs: 1000 } as const;

export class SharedClock {
  /** Server time less local time: as it is told, and as it is best known. */
  private offset = 0;
  private known = 0;
  private told = false;
  private at = 0;
  private heard: { at: number; offset: number }[] = [];
  private cityOffset = 0;
  private animationOffset = 0;
  private roundTrip = Infinity;

  constructor(private readonly now = () => performance.now()) {}

  receive(server: number, city: number, animation: number): void {
    this.hear(server);
    this.cityOffset = city - server;
    this.animationOffset = animation - server / 1000;
  }

  pong(sent: number, server: number): void {
    const elapsed = this.now() - sent;
    if (elapsed < 0) return;
    this.roundTrip = Math.min(this.roundTrip, elapsed);
    this.hear(server);
  }

  serverEpoch(): number {
    const now = this.now();
    const step = (now - this.at) * CLOCK.slew;
    this.offset += Math.max(-step, Math.min(step, this.known - this.offset));
    this.at = now;
    return now + this.offset;
  }

  cityEpoch(): number {
    return this.serverEpoch() + this.cityOffset;
  }

  animationSeconds(): number {
    return this.serverEpoch() / 1000 + this.animationOffset;
  }

  /* A time the server stamped as it sent, heard now: half the shortest round trip ago. */
  private hear(server: number): void {
    const now = this.now();
    const way = Number.isFinite(this.roundTrip) ? this.roundTrip / 2 : 0;
    this.heard.push({ at: now, offset: server + way - now });
    this.heard = this.heard.filter((one) => one.at >= now - CLOCK.windowMs);
    this.known = Math.max(...this.heard.map((one) => one.offset));
    if (this.told && Math.abs(this.known - this.offset) <= CLOCK.stepMs) return;
    this.offset = this.known;
    this.at = now;
    this.told = true;
  }
}
